import { useState } from "react";
import { Hand, Bookmark, Play, MousePointer2, X, ChevronRight, ChevronLeft } from "lucide-react";

const STORAGE_KEY = "quran_reader_tutorial_done";

const STEPS = [
  {
    icon: Hand,
    title: "Tap an ayah",
    body: "In Surah Mode it plays; in Mushaf Page Mode it starts a selection. Tap a second ayah to highlight everything in between.",
    demo: ({ pulse }) => (
      <div className="flex items-center gap-3 font-amiri text-2xl text-[#E8D5A3]" style={{ direction: "rtl" }}>
        <span className={`px-2 py-1 rounded transition-all ${pulse ? "bg-[#C9A227]/40 ring-2 ring-[#C9A227]/60 scale-110" : ""}`}>
          ٱلْحَمْدُ
        </span>
        <span className="opacity-50">لِلَّهِ</span>
        <span className="opacity-50">رَبِّ</span>
      </div>
    ),
  },
  {
    icon: Bookmark,
    title: "Double-tap to bookmark",
    body: "Two quick taps on any ayah opens the bookmark dialog. (On desktop you can also right-click.)",
    demo: ({ pulse }) => (
      <div className="relative flex items-center gap-3 font-amiri text-2xl text-[#E8D5A3]" style={{ direction: "rtl" }}>
        <span className={`px-2 py-1 rounded ${pulse ? "bg-[#C9A227]/40" : ""} transition-all`}>وَٱلتِّينِ</span>
        {pulse && (
          <Bookmark className="absolute -top-3 -right-3 w-5 h-5 text-[#C9A227] animate-bounce" fill="#C9A227" />
        )}
      </div>
    ),
  },
  {
    icon: Play,
    title: "Tap to play (Page Mode)",
    body: "In Mushaf Page Mode, tap any ayah to start audio from that ayah. Long-press an ayah to begin a range selection instead.",
    demo: ({ pulse }) => (
      <div className="flex items-center gap-3 font-amiri text-2xl text-[#E8D5A3]" style={{ direction: "rtl" }}>
        <span className={`px-2 py-1 rounded transition-all ${pulse ? "bg-[#C9A227]/40 ring-4 ring-[#C9A227]/30" : ""}`}>
          إِنَّآ
        </span>
        <span className="opacity-50">أَنزَلْنَٰهُ</span>
      </div>
    ),
  },
  {
    icon: MousePointer2,
    title: "Range actions",
    body: "Once you've selected a range in Page Mode, the floating toolbar lets you Play, Memorize, or Bookmark all selected ayahs at once.",
    demo: () => (
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#1B4D3E]/95 border border-[#C9A227]/40 text-[#E8D5A3] text-xs font-outfit">
        <span>3 ayahs selected</span>
        <span className="px-2 py-0.5 rounded-full bg-[#C9A227] text-[#0D2818] font-semibold">▶ Play</span>
        <span className="px-2 py-0.5 rounded-full border border-[#C9A227]/60">✓ Memorize</span>
        <span className="px-2 py-0.5 rounded-full border border-[#C9A227]/60">☆ Bookmark</span>
      </div>
    ),
  },
];

export function shouldShowTutorial() {
  if (typeof window === "undefined") return false;
  return localStorage.getItem(STORAGE_KEY) !== "1";
}

export function markTutorialSeen() {
  if (typeof window !== "undefined") {
    localStorage.setItem(STORAGE_KEY, "1");
  }
}

export function clearTutorialFlag() {
  if (typeof window !== "undefined") {
    localStorage.removeItem(STORAGE_KEY);
  }
}

export default function ReaderTutorial({ open, onClose }) {
  const [step, setStep] = useState(0);
  const [pulse, setPulse] = useState(true);

  if (!open) return null;

  const total = STEPS.length;
  const Current = STEPS[step];
  const Icon = Current.icon;
  const isLast = step === total - 1;

  const skip = () => {
    markTutorialSeen();
    onClose();
  };

  const prev = () => {
    setPulse(false);
    setTimeout(() => setPulse(true), 30);
    setStep((s) => Math.max(0, s - 1));
  };

  const next = () => {
    if (isLast) {
      markTutorialSeen();
      onClose();
      return;
    }
    setPulse(false);
    setTimeout(() => setPulse(true), 30);
    setStep((s) => s + 1);
  };

  return (
    <div
      data-testid="reader-tutorial"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
    >
      <div className="relative w-full max-w-md rounded-2xl bg-[#0D2818] border border-[#C9A227]/40 shadow-2xl p-6 sm:p-8 text-[#E8D5A3]">
        <button
          data-testid="tutorial-close-btn"
          onClick={skip}
          className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors"
          aria-label="Close tutorial"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Step badge */}
        <div className="flex items-center gap-2 mb-4">
          <div className="w-9 h-9 rounded-full bg-[#C9A227]/20 border border-[#C9A227]/50 flex items-center justify-center">
            <Icon className="w-4 h-4 text-[#C9A227]" />
          </div>
          <span className="text-xs font-outfit text-[#8B9D83] tracking-widest uppercase">
            Step {step + 1} of {total}
          </span>
        </div>

        {/* Animated demo */}
        <div className="bg-[#FBF6E9]/[0.04] border border-[#C9A227]/15 rounded-xl py-8 px-4 mb-5 flex items-center justify-center min-h-[120px]">
          <Current.demo pulse={pulse} />
        </div>

        <h3 className="text-xl font-cinzel mb-2">{Current.title}</h3>
        <p className="text-sm font-outfit text-[#E8D5A3]/80 leading-relaxed mb-6">
          {Current.body}
        </p>

        {/* Progress dots */}
        <div className="flex items-center justify-center gap-1.5 mb-5">
          {STEPS.map((s, i) => (
            <div
              key={`step-dot-${s.title}`}
              className={`h-1.5 rounded-full transition-all ${
                i === step
                  ? "w-6 bg-[#C9A227]"
                  : i < step
                  ? "w-1.5 bg-[#C9A227]/60"
                  : "w-1.5 bg-[#C9A227]/20"
              }`}
            />
          ))}
        </div>

        {/* Footer buttons */}
        <div className="flex items-center justify-between">
          <button
            data-testid="tutorial-skip-btn"
            onClick={skip}
            className="text-xs text-[#8B9D83] hover:text-[#E8D5A3] transition-colors font-outfit"
          >
            Skip
          </button>
          <div className="flex items-center gap-2">
            {step > 0 && (
              <button
                data-testid="tutorial-prev-btn"
                onClick={prev}
                className="flex items-center gap-1 px-3 py-1.5 rounded-full border border-[#C9A227]/40 text-xs font-outfit hover:bg-white/5 transition-colors"
              >
                <ChevronLeft className="w-3 h-3" /> Back
              </button>
            )}
            <button
              data-testid="tutorial-next-btn"
              onClick={next}
              className="flex items-center gap-1 px-4 py-1.5 rounded-full bg-[#C9A227] text-[#0D2818] text-xs font-semibold hover:bg-[#E8C75B] transition-colors"
            >
              {isLast ? "Got it!" : (
                <>
                  Next <ChevronRight className="w-3 h-3" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
